// Crew personal data: one rule for the Crew pages, the AI and the database (sql/test-fixes-5.sql: crew_private, crew_full).
// A crew login always sees its OWN record in full - its crew record is linked to the login by crew_id (crew_accounts.crew_id
// = crew.id), never by name. Other crew members' personal data (passport / ID, contact, date and place of birth, nationality,
// seaman's book, next of kin, notes) and their crew documents (passport, medical, certificates) need the "Crew personal data"
// right (crew_personal, View or more: Chief Officer, Chief Engineer, Chief Stew / Purser presets). Crew Edit alone does not.
// The Captain (vessel account) and the management company always have it. Everyone with Crew view still sees the crew list:
// name, position, department, on board, contract. The database enforces this; the app and the AI apply it too.
export const PERSONAL_MODULE = 'crew_personal'
// the columns that live in crew_private (same list as sql/test-fixes-5.sql)
export const PERSONAL_FIELDS = ['nationality', 'phone', 'email', 'date_of_birth', 'place_of_birth', 'passport_no', 'passport_issue_place',
  'passport_issued_date', 'passport_expiry_date', 'seamans_book_no', 'seamans_book_expiry_date', 'emergency_contact_name',
  'emergency_contact_phone', 'notes']
// the columns of crew logins may read (sql/test-fixes-5-lock.sql: every column but the personal ones). select=* on crew is
// refused for every login - read crew_full, or crew with these columns only
export const CREW_COLUMNS = ['id', 'client_id', 'crew_id', 'first_name', 'last_name', 'name', 'position', 'department', 'status',
  'contract_start_date', 'contract_end_date', 'onboard', 'photo_url', 'created_at', 'archived', 'leave_entitlement_days']
// crew_full named in full (not select=*): a column added to crew later without the login's column right cannot break the page
export const CREW_FULL_SELECT = [...CREW_COLUMNS, ...PERSONAL_FIELDS].join(',')
// a select list for crew_full ('*' = every column) and the same list cut to what crew itself allows
export const crewFullSelect = cols => (!cols || String(cols).trim() === '*' ? CREW_FULL_SELECT : String(cols))
export function crewSelect(cols) {
  const want = crewFullSelect(cols).split(',').map(c => c.trim()).filter(c => CREW_COLUMNS.includes(c))
  return (want.length ? want : ['id']).join(',')
}
// the ones a crew member may change on its own record without Crew Edit
export const OWN_CONTACT_FIELDS = ['phone', 'email', 'emergency_contact_name', 'emergency_contact_phone']
export const HIDDEN_NOTE = 'Personal data of other crew members is hidden for this login (it may only see its own). Tell the user plainly; the Captain can give the "Crew personal data" right on the Crew Logins page.'

const LV = { none: 0, read: 1, write: 2, full: 3 }

// may this login see other crew members' personal data? (not a crew login = Captain / management / admin: yes)
export function canSeeOthers(accountType, permissions) {
  if (accountType !== 'crew') return true
  return (LV[(permissions || {})[PERSONAL_MODULE]] || 0) >= 1
}

// own crew record / own document: by the login's crew_id (null = the login is not linked: nothing is "own")
export const isOwnCrew = (row, crewId) => !!crewId && !!row && String(row.id) === String(crewId)
export const isOwnDoc = (doc, crewId) => !!crewId && !!doc && String(doc.crew_id || doc['Crew Record'] || '') === String(crewId)

// a crew row as this login may see it: own or allowed = as is; else the personal fields removed
export function redactCrew(row, crewId, allowed) {
  if (!row || allowed || isOwnCrew(row, crewId)) return row
  const out = { ...row }
  let hid = false
  for (const k of PERSONAL_FIELDS) if (k in out) { if (out[k] != null && out[k] !== '') hid = true; delete out[k] }
  if (hid || PERSONAL_FIELDS.some(k => k in row)) out.personal_data = 'hidden'
  return out
}
export const visibleDocs = (docs, crewId, allowed) => (allowed ? docs : (docs || []).filter(d => isOwnDoc(d, crewId)))

// a crew row (database names) split for writing: base columns -> crew, personal columns -> crew_private
export function splitCrewRow(row) {
  const base = {}, priv = {}
  for (const [k, v] of Object.entries(row || {})) (PERSONAL_FIELDS.includes(k) ? priv : base)[k] = v
  return { base, priv, hasPriv: Object.keys(priv).length > 0 }
}

// the crew record a NEW login belongs to when the Captain did not pick one: every word of the login name is in exactly one
// crew member's name (a one-word login name must equal a one-word crew name) - same rule as sql/test-fixes-5.sql; else null
const words = v => String(v || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim().split(' ').filter(Boolean)
export function crewIdForLoginName(crew, loginName) {
  const m = words(loginName)
  if (!m.length) return null
  const hits = (crew || []).filter(c => !c.archived).filter(c => {
    const n = words(((c.first_name || '') + ' ' + (c.last_name || '')).trim() || c.name)
    return m.length === 1 ? n.length === 1 && n[0] === m[0] : m.every(w => n.includes(w))
  })
  return hits.length === 1 ? hits[0].id : null
}
